import { Divider, flex, Group, Text } from '@byecode/ui'
import type { BackgroundProtocol, EdgeValue } from '@lighthouse/core'
import { BACKGROUND_TYPE, TABS_TYPE } from '@lighthouse/core'
import {
    emptyNumberField,
    getInputInnerTypeMatch,
    getIsFindUseInRichText,
    ListItem4ByecodeUi,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit, PositionSetting, SizeSetting } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { DesignSetting } from '@/components/StyleSetting/DesignSetting'
import { FontSetting } from '@/components/StyleSetting/FontSetting'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import FormSegmentedControl from '../Common/FormSegmentedControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM, POSITION_ALIGN_CONTROL } from '../constants'
import TabBarSettings from './TabBarSettings'
import { TabsTypeSelector } from './TabsTypeSelector'

const SCxRightFill = styled.div`
    width: 180px;
    /* max-width: 80%; */
    ${flex}
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
`

const initTabStyleConfig: Record<string, { background?: BackgroundProtocol; radius: EdgeValue }> = {
    LINE: {
        radius: [0, 0, 0, 0]
    },
    ROUND: {
        radius: [100, 100, 100, 100]
    },
    CARD_SLOT: {
        radius: [6, 6, 0, 0]
    },
    SEGMENTED: {
        background: {
            type: 'color',
            color: '#eceff4'
        },
        radius: [100, 100, 100, 100]
    }
}

export const TabsSettings: React.FC = () => {
    const { handleSetBreakKeys } = useSetExtendsKeys()
    const styleProps = getBlockAndPageDesignLimit('tabs')
    const { control, watch, setValue } = useFormContext()
    const [variant] = watch(['variant'])
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    // const isLineType = variant === TABS_TYPE.LINE
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()
    const isSegmentedType = variant === TABS_TYPE.SEGMENTED
    const findUseObject = useFindUseObjectContext()
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const disabledWithVersion = useIsDisabledWithVersion()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    useEffect(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <Group label="标签类型">
                        <ListItem4ByecodeUi grow>
                            <Controller
                                name="variant"
                                control={control}
                                render={({ field }) => (
                                    <TabsTypeSelector
                                        name="variant"
                                        value={field.value}
                                        onChange={val => {
                                            const init = initTabStyleConfig[val]
                                            setValue('breakPoint.design.background', init.background)
                                            setValue('breakPoint.design.radius', init.radius)
                                            field.onChange(val)
                                            handleSetBreakKeys(['breakPoint.design.background', 'breakPoint.design.radius'])
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </Group>

                    <Divider />
                    <Group label="默认">
                        <ListItem4ByecodeUi
                            alignItems="center"
                            justifyContent="space-between"
                            styles={{
                                root: {
                                    gap: 0
                                }
                            }}
                        >
                            <Text color="var(--color-gray-600)">默认选中标签</Text>
                            <SCxRightFill>
                                <Controller
                                    control={control}
                                    name="defaultTabIndex"
                                    render={({ field }) => {
                                        const isHighLight = getIsFindUseInRichText({
                                            doc: field.value,
                                            findUseObject
                                        })
                                        return (
                                            <VariableSelect
                                                width="100%"
                                                field={emptyNumberField}
                                                value={field.value}
                                                innerTypes={getInputInnerTypeMatch('NUMBER')}
                                                onChange={field.onChange}
                                                viewOptions={customViewOption ? [customViewOption] : undefined}
                                                pageOptions={pageOptions}
                                                userOption={userOption}
                                                dataSourceList={dataSourceList}
                                                enableFormula
                                            />
                                        )
                                    }}
                                />
                            </SCxRightFill>
                        </ListItem4ByecodeUi>
                    </Group>

                    <Divider />
                    <>
                        <PositionSetting {...styleProps.position} />
                        <Divider />
                        <SizeSetting {...styleProps.size} />
                        <Divider />
                        <Group label="布局">
                            <Controller
                                control={control}
                                name={`variantMap.${variant}.align`}
                                render={({ field }) => (
                                    <FormSegmentedControl
                                        {...field}
                                        style={{ flex: '0 0 180px' }}
                                        label="对齐"
                                        data={POSITION_ALIGN_CONTROL}
                                    />
                                )}
                            />
                        </Group>

                        <Divider />
                        <Group label="样式">
                            <DesignSetting
                                {...styleProps.design}
                                enableLineColor={variant === TABS_TYPE.LINE}
                                colorSetting={{
                                    background: [BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient, BACKGROUND_TYPE.variable]
                                }}
                            />
                        </Group>
                    </>

                    <Divider />

                    <FontSetting {...styleProps.font} />

                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="标签">
                        <TabBarSettings variant={variant} isSegmentedType={isSegmentedType} />
                    </Group>
                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
